import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import DigitalCardExport from './components/DigitalCardExport';

// ─── Slug resolver (mirrors DigitalCardExport) ────────────────────────────────

const SLUG_MAP: Record<string, string> = {
  hector: 'hector',
};

function getCardSlug(name: string): string | null {
  const lower = name.trim().toLowerCase();
  for (const key of Object.keys(SLUG_MAP)) {
    if (lower.includes(key)) return SLUG_MAP[key];
  }
  return null;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CardEntry {
  id: string;
  card: CardData;
  variant: DesignVariant;
  qty: number; // how many copies on the sheet
}

interface CardData {
  name: string;
  title: string;
  email: string;
  phone: string;
  website: string;
  tagline: string;
}

type DesignVariant = 'gold-on-dark' | 'dark-on-white' | 'dark-on-cream' | 'minimal-dark';
type CardSide = 'front' | 'back';

// ─── Constants ────────────────────────────────────────────────────────────────

const CARD_W = 3.5;
const CARD_H = 2.0;
const BLEED = 0.125;
const SHEET_SLOTS = 5; // rows per column on US Letter

const designVariants: { id: DesignVariant; label: string; desc: string }[] = [
  { id: 'gold-on-dark', label: 'Gold on Dark', desc: 'Charcoal, gold monogram' },
  { id: 'dark-on-white', label: 'Dark on White', desc: 'Clean white, charcoal' },
  { id: 'dark-on-cream', label: 'Dark on Cream', desc: 'Warm cream, charcoal' },
  { id: 'minimal-dark', label: 'Minimal Dark', desc: 'Full black, white' },
];

const defaultCard: CardData = {
  name: 'Hector',
  title: 'Principal & Managing Director',
  email: 'Hector.S@higherstandardglobal.com',
  phone: '+1 (307) 218 3610',
  website: 'higherstandardglobal.com',
  tagline: 'By Reference Only',
};

const teamPresets: Omit<CardEntry, 'id'>[] = [
  {
    card: { name: 'Hector', title: 'Principal & Managing Director', email: 'Hector.S@higherstandardglobal.com', phone: '+1 (307) 218 3610', website: 'higherstandardglobal.com', tagline: 'By Reference Only' },
    variant: 'gold-on-dark', qty: 2,
  },
  {
    card: { name: 'Alexandra Whitmore', title: 'Director of Property Relations', email: 'a.whitmore@higherstandardglobal.com', phone: '+1 (310) 555 0192', website: 'higherstandardglobal.com', tagline: 'By Reference Only' },
    variant: 'gold-on-dark', qty: 2,
  },
  {
    card: { name: 'James Harrington', title: 'Senior Property Manager', email: 'j.harrington@higherstandardglobal.com', phone: '+1 (310) 555 0184', website: 'higherstandardglobal.com', tagline: 'By Reference Only' },
    variant: 'gold-on-dark', qty: 1,
  },
];

let idCounter = 0;
const uid = () => `card-${++idCounter}`;

// ─── Design Configs ───────────────────────────────────────────────────────────

function getDesignConfig(variant: DesignVariant) {
  switch (variant) {
    case 'gold-on-dark':
      return { frontBg: '#2C2C2C', backBg: '#1A1A1A', monogram: '#C9A96E', name: '#FFFFFF', title: '#C9A96E', body: 'rgba(255,255,255,0.55)', divider: 'rgba(201,169,110,0.3)', tagline: 'rgba(201,169,110,0.6)', wordmark1: '#C9A96E', wordmark2: '#FFFFFF', accentLine: '#C9A96E' };
    case 'dark-on-white':
      return { frontBg: '#FFFFFF', backBg: '#F7F4EF', monogram: '#2C2C2C', name: '#1A1A1A', title: '#8B7355', body: '#6B6B6B', divider: 'rgba(44,44,44,0.15)', tagline: '#9A9A9A', wordmark1: '#2C2C2C', wordmark2: '#8B7355', accentLine: '#C9A96E' };
    case 'dark-on-cream':
      return { frontBg: '#F7F4EF', backBg: '#EDE9E2', monogram: '#2C2C2C', name: '#1A1A1A', title: '#8B7355', body: '#6B6B6B', divider: 'rgba(44,44,44,0.15)', tagline: '#9A9A9A', wordmark1: '#2C2C2C', wordmark2: '#8B7355', accentLine: '#8B7355' };
    case 'minimal-dark':
      return { frontBg: '#0F0F0F', backBg: '#0F0F0F', monogram: '#FFFFFF', name: '#FFFFFF', title: 'rgba(255,255,255,0.5)', body: 'rgba(255,255,255,0.4)', divider: 'rgba(255,255,255,0.1)', tagline: 'rgba(255,255,255,0.25)', wordmark1: '#FFFFFF', wordmark2: 'rgba(255,255,255,0.5)', accentLine: 'rgba(255,255,255,0.2)' };
  }
}

// ─── Card Face Components ─────────────────────────────────────────────────────

function CardFront({ card, variant, scale = 1 }: { card: CardData; variant: DesignVariant; scale?: number }) {
  const cfg = getDesignConfig(variant);
  const W = CARD_W * 96 * scale;
  const H = CARD_H * 96 * scale;
  const pad = 22 * scale;
  const fs = (n: number) => n * scale;

  return (
    <div style={{ width: W, height: H, backgroundColor: cfg.frontBg, borderRadius: 4 * scale, padding: pad, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden', flexShrink: 0, boxSizing: 'border-box' }}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: 60 * scale, height: 60 * scale, borderBottom: `1px solid ${cfg.accentLine}`, borderLeft: `1px solid ${cfg.accentLine}`, opacity: 0.3, borderBottomLeftRadius: 40 * scale }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 * scale }}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: fs(20), fontWeight: 600, letterSpacing: '0.15em', color: cfg.monogram, lineHeight: 1 }}>HSG</span>
        <div style={{ width: 1, height: 22 * scale, backgroundColor: cfg.divider }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 * scale }}>
          <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: fs(6.5), fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', color: cfg.wordmark1, lineHeight: 1 }}>Higher Standard</span>
          <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: fs(6.5), fontWeight: 300, letterSpacing: '0.35em', textTransform: 'uppercase', color: cfg.wordmark2, lineHeight: 1 }}>Global</span>
        </div>
      </div>
      <div>
        <div style={{ marginBottom: 10 * scale }}>
          <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: fs(13), fontWeight: 500, color: cfg.name, letterSpacing: '0.04em', lineHeight: 1.2, margin: 0 }}>{card.name || 'Your Name'}</p>
          <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: fs(6.5), fontWeight: 400, color: cfg.title, letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0, marginTop: 4 * scale }}>{card.title || 'Your Title'}</p>
        </div>
        <div style={{ height: 1, backgroundColor: cfg.divider, marginBottom: 10 * scale }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 * scale }}>
          {card.email && <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: fs(6), color: cfg.body, letterSpacing: '0.04em', margin: 0 }}>{card.email}</p>}
          <div style={{ display: 'flex', gap: 16 * scale }}>
            {card.phone && <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: fs(6), color: cfg.body, letterSpacing: '0.04em', margin: 0 }}>{card.phone}</p>}
            {card.website && <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: fs(6), color: cfg.body, letterSpacing: '0.04em', margin: 0 }}>{card.website}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CardBack({ card, variant, scale = 1 }: { card: CardData; variant: DesignVariant; scale?: number }) {
  const cfg = getDesignConfig(variant);
  const W = CARD_W * 96 * scale;
  const H = CARD_H * 96 * scale;
  const fs = (n: number) => n * scale;

  return (
    <div style={{ width: W, height: H, backgroundColor: cfg.backBg, borderRadius: 4 * scale, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', flexShrink: 0, boxSizing: 'border-box' }}>
      {[{ top: 12 * scale, left: 12 * scale }, { top: 12 * scale, right: 12 * scale }, { bottom: 12 * scale, left: 12 * scale }, { bottom: 12 * scale, right: 12 * scale }].map((pos, i) => (
        <div key={i} style={{ position: 'absolute', width: 18 * scale, height: 18 * scale, borderTop: `1px solid ${cfg.accentLine}`, borderLeft: `1px solid ${cfg.accentLine}`, opacity: 0.5, ...pos }} />
      ))}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 * scale }}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: fs(36), fontWeight: 600, letterSpacing: '0.2em', color: cfg.monogram, lineHeight: 1 }}>HSG</span>
        <div style={{ width: 40 * scale, height: 1, backgroundColor: cfg.accentLine, opacity: 0.5 }} />
        <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: fs(6), fontWeight: 400, letterSpacing: '0.3em', textTransform: 'uppercase', color: cfg.tagline, margin: 0, textAlign: 'center' }}>{card.tagline || 'By Reference Only'}</p>
      </div>
    </div>
  );
}

// ─── Multi-card Print Sheet ───────────────────────────────────────────────────

function MultiPrintSheet({ entries }: { entries: CardEntry[] }) {
  const sheetW = 8.5 * 96;
  const sheetH = 11 * 96;
  const cardW = (CARD_W + BLEED * 2) * 96;
  const cardH = (CARD_H + BLEED * 2) * 96;
  const cols = 2;
  const rows = SHEET_SLOTS;
  const marginX = (sheetW - cols * cardW) / 2;
  const marginY = (sheetH - rows * cardH) / 2;

  // Expand entries by qty into a flat slot list (max 5 slots)
  const slots: CardEntry[] = [];
  for (const entry of entries) {
    for (let q = 0; q < entry.qty && slots.length < SHEET_SLOTS; q++) {
      slots.push(entry);
    }
  }
  while (slots.length < SHEET_SLOTS) slots.push(slots[0] ?? entries[0]);

  const markColor = '#CCCCCC';
  const ms = { position: 'absolute' as const, backgroundColor: markColor };

  return (
    <div style={{ width: sheetW, height: sheetH, backgroundColor: '#FFFFFF', position: 'relative', flexShrink: 0 }}>
      {slots.map((_, row) => {
        return [0, 1].map((col) => {
          const x = marginX + col * cardW;
          const y = marginY + row * cardH;
          const ml = 8, mg = 4;
          return (
            <div key={`mark-${row}-${col}`}>
              <div style={{ ...ms, left: x - mg - ml, top: y, width: ml, height: 0.5 }} />
              <div style={{ ...ms, left: x, top: y - mg - ml, width: 0.5, height: ml }} />
              <div style={{ ...ms, left: x + cardW + mg, top: y, width: ml, height: 0.5 }} />
              <div style={{ ...ms, left: x + cardW, top: y - mg - ml, width: 0.5, height: ml }} />
              <div style={{ ...ms, left: x - mg - ml, top: y + cardH, width: ml, height: 0.5 }} />
              <div style={{ ...ms, left: x, top: y + cardH + mg, width: 0.5, height: ml }} />
              <div style={{ ...ms, left: x + cardW + mg, top: y + cardH, width: ml, height: 0.5 }} />
              <div style={{ ...ms, left: x + cardW, top: y + cardH + mg, width: 0.5, height: ml }} />
            </div>
          );
        });
      })}
      {slots.map((entry, row) => {
        const frontX = marginX + 0 * cardW + BLEED * 96;
        const backX = marginX + 1 * cardW + BLEED * 96;
        const y = marginY + row * cardH + BLEED * 96;
        return (
          <div key={`slot-${row}`}>
            <div style={{ position: 'absolute', left: frontX, top: y }}>
              <CardFront card={entry.card} variant={entry.variant} scale={1} />
            </div>
            <div style={{ position: 'absolute', left: backX, top: y }}>
              <CardBack card={entry.card} variant={entry.variant} scale={1} />
            </div>
          </div>
        );
      })}
      <p style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', fontFamily: 'Inter, Arial, sans-serif', fontSize: 8, color: '#CCCCCC', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
        Higher Standard Global · Business Card · 3.5" × 2" · Print at 300 DPI
      </p>
    </div>
  );
}

// ─── Card Editor Row ──────────────────────────────────────────────────────────

function CardEditorRow({
  entry,
  index,
  total,
  isExpanded,
  onToggle,
  onChange,
  onDuplicate,
  onRemove,
  onVariantChange,
  onQtyChange,
}: {
  entry: CardEntry;
  index: number;
  total: number;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (field: keyof CardData, value: string) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onVariantChange: (v: DesignVariant) => void;
  onQtyChange: (q: number) => void;
}) {
  const cfg = getDesignConfig(entry.variant);
  const isDark = entry.variant === 'dark-on-white' || entry.variant === 'dark-on-cream';

  const fields: { field: keyof CardData; label: string; placeholder: string; icon: string }[] = [
    { field: 'name', label: 'Full Name', placeholder: 'Full Name', icon: 'ri-user-line' },
    { field: 'title', label: 'Job Title', placeholder: 'Job Title', icon: 'ri-briefcase-line' },
    { field: 'email', label: 'Email', placeholder: 'email@higherstandardglobal.com', icon: 'ri-mail-line' },
    { field: 'phone', label: 'Phone', placeholder: '+1 (310) 555 0000', icon: 'ri-phone-line' },
    { field: 'website', label: 'Website', placeholder: 'higherstandardglobal.com', icon: 'ri-global-line' },
    { field: 'tagline', label: 'Back Tagline', placeholder: 'By Reference Only', icon: 'ri-text' },
  ];

  return (
    <div className={`bg-white rounded-lg border transition-all ${isExpanded ? 'border-[#C9A96E]' : 'border-[#E8E6E3]'}`}>
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={onToggle}
      >
        {/* Mini card swatch */}
        <div
          className="w-10 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: cfg.frontBg }}
        >
          <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 9, fontWeight: 600, letterSpacing: '0.1em', color: cfg.monogram }}>HSG</span>
        </div>

        {/* Name + title */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#1A1A1A] truncate">{entry.card.name || 'Unnamed'}</p>
          <p className="text-[10px] text-[#9A9A9A] truncate">{entry.card.title || 'No title'}</p>
        </div>

        {/* Qty badge */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] text-[#9A9A9A]">×{entry.qty}</span>
          <span className="text-[10px] text-[#C9A96E] bg-[#C9A96E]/10 px-1.5 py-0.5 rounded-full border border-[#C9A96E]/20">
            {designVariants.find(d => d.id === entry.variant)?.label}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onDuplicate}
            title="Duplicate card"
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-[#F7F4EF] text-[#9A9A9A] hover:text-[#2C2C2C] transition-colors cursor-pointer"
          >
            <i className="ri-file-copy-line text-xs"></i>
          </button>
          {total > 1 && (
            <button
              onClick={onRemove}
              title="Remove card"
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-[#9A9A9A] hover:text-red-500 transition-colors cursor-pointer"
            >
              <i className="ri-delete-bin-line text-xs"></i>
            </button>
          )}
          <div className="w-7 h-7 flex items-center justify-center text-[#9A9A9A]">
            <i className={`text-xs transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </div>
        </div>
      </div>

      {/* Expanded editor */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-[#F7F4EF]">
          {/* Variant + qty row */}
          <div className="flex gap-3 mt-4 mb-4">
            <div className="flex-1">
              <label className="block text-[10px] text-[#9A9A9A] mb-1.5 uppercase tracking-wide">Design</label>
              <select
                value={entry.variant}
                onChange={(e) => onVariantChange(e.target.value as DesignVariant)}
                className="w-full px-3 py-2 text-xs text-[#1A1A1A] bg-[#FAFAF8] border border-[#E8E6E3] rounded-md focus:outline-none focus:border-[#C9A96E] cursor-pointer"
              >
                {designVariants.map(d => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </div>
            <div className="w-24">
              <label className="block text-[10px] text-[#9A9A9A] mb-1.5 uppercase tracking-wide">Copies</label>
              <div className="flex items-center border border-[#E8E6E3] rounded-md overflow-hidden bg-[#FAFAF8]">
                <button
                  onClick={() => onQtyChange(Math.max(1, entry.qty - 1))}
                  className="w-8 h-8 flex items-center justify-center text-[#9A9A9A] hover:text-[#2C2C2C] hover:bg-[#F0EDE8] transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-subtract-line text-xs"></i>
                </button>
                <span className="flex-1 text-center text-xs font-medium text-[#1A1A1A]">{entry.qty}</span>
                <button
                  onClick={() => onQtyChange(Math.min(5, entry.qty + 1))}
                  className="w-8 h-8 flex items-center justify-center text-[#9A9A9A] hover:text-[#2C2C2C] hover:bg-[#F0EDE8] transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-add-line text-xs"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-2.5">
            {fields.map(({ field, label, placeholder, icon }) => (
              <div key={field}>
                <label className="block text-[10px] text-[#9A9A9A] mb-1 tracking-wide">{label}</label>
                <div className="relative">
                  <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#C8C5C0]">
                    <i className={`${icon} text-xs`}></i>
                  </div>
                  <input
                    type="text"
                    value={entry.card[field]}
                    onChange={(e) => onChange(field, e.target.value)}
                    placeholder={placeholder}
                    className="w-full pl-7 pr-3 py-1.5 text-xs text-[#1A1A1A] bg-[#FAFAF8] border border-[#E8E6E3] rounded-md focus:outline-none focus:border-[#C9A96E] transition-colors placeholder:text-[#D0CCC7]"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Mini preview */}
          <div className="mt-4 flex gap-3 items-start">
            <div>
              <p className="text-[9px] text-[#9A9A9A] uppercase tracking-widest mb-1">Front</p>
              <div className="rounded overflow-hidden" style={{ padding: 6, background: isDark ? '#E8E6E3' : '#3A3A3A' }}>
                <CardFront card={entry.card} variant={entry.variant} scale={0.55} />
              </div>
            </div>
            <div>
              <p className="text-[9px] text-[#9A9A9A] uppercase tracking-widest mb-1">Back</p>
              <div className="rounded overflow-hidden" style={{ padding: 6, background: isDark ? '#D8D5D0' : '#2A2A2A' }}>
                <CardBack card={entry.card} variant={entry.variant} scale={0.55} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sheet Slot Visualizer ────────────────────────────────────────────────────

function SheetSlotVisualizer({ entries }: { entries: CardEntry[] }) {
  const slots: (CardEntry | null)[] = [];
  for (const entry of entries) {
    for (let q = 0; q < entry.qty && slots.length < SHEET_SLOTS; q++) {
      slots.push(entry);
    }
  }
  const used = slots.length;
  while (slots.length < SHEET_SLOTS) slots.push(null);

  return (
    <div className="bg-white rounded-lg border border-[#E8E6E3] p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#9A9A9A]">Sheet Layout</p>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${used >= SHEET_SLOTS ? 'bg-[#C9A96E]/15 text-[#8B7355]' : 'bg-[#F7F4EF] text-[#9A9A9A]'}`}>
          {used}/{SHEET_SLOTS} slots used
        </span>
      </div>
      <div className="space-y-1.5">
        {slots.map((entry, i) => {
          const cfg = entry ? getDesignConfig(entry.variant) : null;
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[9px] text-[#C8C5C0] w-3 text-right flex-shrink-0">{i + 1}</span>
              {entry && cfg ? (
                <div className="flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-md" style={{ backgroundColor: cfg.frontBg + '22', border: `1px solid ${cfg.frontBg}33` }}>
                  <div className="w-5 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cfg.frontBg }}>
                    <span style={{ display: 'block', fontFamily: 'Georgia, serif', fontSize: 4, fontWeight: 700, color: cfg.monogram, textAlign: 'center', lineHeight: '12px', letterSpacing: '0.05em' }}>HSG</span>
                  </div>
                  <p className="text-[10px] text-[#1A1A1A] truncate flex-1">{entry.card.name || 'Unnamed'}</p>
                  <span className="text-[9px] text-[#9A9A9A] flex-shrink-0">{designVariants.find(d => d.id === entry.variant)?.label}</span>
                </div>
              ) : (
                <div className="flex-1 px-2.5 py-1.5 rounded-md border border-dashed border-[#E8E6E3] flex items-center">
                  <p className="text-[10px] text-[#C8C5C0]">Empty slot</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {used >= SHEET_SLOTS && (
        <p className="text-[10px] text-[#8B7355] mt-2 leading-relaxed">
          Sheet is full. Additional cards will print on a second sheet.
        </p>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function BusinessCardPage() {
  useSEO({
    title: 'Business Card Designer | Higher Standard Global',
    description: 'Design and print business cards for the HSG team.',
    canonical: '/business-card',
    noIndex: true,
  });

  const navigate = useNavigate();
  const [entries, setEntries] = useState<CardEntry[]>(() =>
    teamPresets.map(p => ({ ...p, id: uid() }))
  );
  const [expandedId, setExpandedId] = useState<string>(entries[0]?.id ?? '');
  const [previewEntry, setPreviewEntry] = useState<CardEntry>(entries[0]);
  const [activeSide, setActiveSide] = useState<CardSide>('front');
  const [showPrintSheet, setShowPrintSheet] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const copyLinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [digitalExportCard, setDigitalExportCard] = useState<CardData | null>(null);

  const handleCopyCardLink = useCallback(async (entry: CardEntry) => {
    const slug = getCardSlug(entry.card.name);
    if (!slug) return;
    const url = `${window.location.origin}/card/${slug}`;
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setCopiedLinkId(entry.id);
    if (copyLinkTimerRef.current) clearTimeout(copyLinkTimerRef.current);
    copyLinkTimerRef.current = setTimeout(() => setCopiedLinkId(null), 2500);
  }, []);

  const totalSlots = entries.reduce((s, e) => s + e.qty, 0);

  const updateEntry = useCallback((id: string, field: keyof CardData, value: string) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, card: { ...e.card, [field]: value } } : e));
    setPreviewEntry(prev => prev.id === id ? { ...prev, card: { ...prev.card, [field]: value } } : prev);
  }, []);

  const duplicateEntry = useCallback((id: string) => {
    setEntries(prev => {
      const idx = prev.findIndex(e => e.id === id);
      if (idx === -1) return prev;
      const src = prev[idx];
      const newEntry: CardEntry = { ...src, id: uid(), card: { ...src.card } };
      const next = [...prev];
      next.splice(idx + 1, 0, newEntry);
      setExpandedId(newEntry.id);
      setPreviewEntry(newEntry);
      return next;
    });
  }, []);

  const removeEntry = useCallback((id: string) => {
    setEntries(prev => {
      const next = prev.filter(e => e.id !== id);
      if (expandedId === id && next.length > 0) {
        setExpandedId(next[0].id);
        setPreviewEntry(next[0]);
      }
      return next;
    });
  }, [expandedId]);

  const addBlankCard = () => {
    const newEntry: CardEntry = {
      id: uid(),
      card: { name: '', title: '', email: '', phone: '', website: 'higherstandardglobal.com', tagline: 'By Reference Only' },
      variant: 'gold-on-dark',
      qty: 1,
    };
    setEntries(prev => [...prev, newEntry]);
    setExpandedId(newEntry.id);
    setPreviewEntry(newEntry);
  };

  const handleToggle = (id: string) => {
    const entry = entries.find(e => e.id === id);
    if (entry) setPreviewEntry(entry);
    setExpandedId(prev => prev === id ? '' : id);
  };

  const handlePrint = () => {
    setShowPrintSheet(true);
    setTimeout(() => window.print(), 300);
  };

  const handleCopySpecs = async () => {
    const lines = entries.map((e, i) =>
      `Card ${i + 1}: ${e.card.name} · ${e.card.title} · ${designVariants.find(d => d.id === e.variant)?.label} · ×${e.qty} copies`
    ).join('\n');
    const text = `Business Card Sheet — Higher Standard Global\nTotal slots: ${totalSlots}/${SHEET_SLOTS}\n\n${lines}\n\nSize: 3.5" × 2" | Bleed: 0.125" | 300 DPI | CMYK`;
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const isDark = previewEntry.variant === 'dark-on-white' || previewEntry.variant === 'dark-on-cream';

  return (
    <>
      {digitalExportCard && (
        <DigitalCardExport
          card={digitalExportCard}
          onClose={() => setDigitalExportCard(null)}
        />
      )}

      {showPrintSheet && (
        <div className="print-only fixed inset-0 z-[9999] bg-white flex items-start justify-center overflow-auto">
          <MultiPrintSheet entries={entries} />
        </div>
      )}
      <style>{`
        @media print { body > * { display: none !important; } .print-only { display: flex !important; position: static !important; } }
        @media screen { .print-only { display: none !important; } }
      `}</style>

      <div className="min-h-screen bg-[#F7F4EF]">

        {/* ── Header ── */}
        <div className="w-full bg-[#1A1A1A] pt-16 pb-12 px-6 md:px-16">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs uppercase tracking-[0.35em] text-[#C9A96E] mb-3 font-medium">Higher Standard Global</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-white mb-3 leading-tight">Business Card</h1>
            <p className="text-sm text-white/40 max-w-md leading-relaxed">
              Add multiple team members, set copies per person, and print a mixed sheet in one go.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <a href="/brand" onClick={(e) => { e.preventDefault(); navigate('/brand'); }} className="flex items-center gap-2 text-xs px-4 py-2 rounded-md border border-white/20 text-white/60 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>Brand Guidelines
              </a>
              <a href="/logo-export" onClick={(e) => { e.preventDefault(); navigate('/logo-export'); }} className="flex items-center gap-2 text-xs px-4 py-2 rounded-md border border-white/20 text-white/60 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-image-line"></i>Logo Export
              </a>
            </div>
          </div>
        </div>

        {/* ── Main ── */}
        <div className="max-w-6xl mx-auto px-6 md:px-16 py-12">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* ── Left: Team roster ── */}
            <div className="w-full lg:w-[360px] flex-shrink-0 space-y-4">

              {/* Roster header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A]">Team Roster</p>
                  <p className="text-[11px] text-[#C8C5C0] mt-0.5">{entries.length} {entries.length === 1 ? 'person' : 'people'} · {totalSlots}/{SHEET_SLOTS} sheet slots</p>
                </div>
                <button
                  onClick={addBlankCard}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#2C2C2C] text-white text-xs font-medium hover:bg-[#C9A96E] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-xs"></i>
                  Add person
                </button>
              </div>

              {/* Card entries */}
              <div className="space-y-2">
                {entries.map((entry, index) => (
                  <CardEditorRow
                    key={entry.id}
                    entry={entry}
                    index={index}
                    total={entries.length}
                    isExpanded={expandedId === entry.id}
                    onToggle={() => handleToggle(entry.id)}
                    onChange={(field, value) => updateEntry(entry.id, field, value)}
                    onDuplicate={() => duplicateEntry(entry.id)}
                    onRemove={() => removeEntry(entry.id)}
                    onVariantChange={(v) => {
                      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, variant: v } : e));
                      if (previewEntry.id === entry.id) setPreviewEntry(prev => ({ ...prev, variant: v }));
                    }}
                    onQtyChange={(q) => {
                      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, qty: q } : e));
                    }}
                  />
                ))}
              </div>

              {/* Sheet slot visualizer */}
              <SheetSlotVisualizer entries={entries} />

              {/* Print specs */}
              <div className="bg-white rounded-lg border border-[#E8E6E3] p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#9A9A9A] mb-3">Print Specs</p>
                <div className="space-y-1.5">
                  {[
                    { label: 'Size', value: '3.5" × 2"' },
                    { label: 'Bleed', value: '0.125" all sides' },
                    { label: 'Resolution', value: '300 DPI min' },
                    { label: 'Layout', value: `${SHEET_SLOTS}-up on US Letter` },
                  ].map(row => (
                    <div key={row.label} className="flex items-center justify-between">
                      <p className="text-[11px] text-[#9A9A9A]">{row.label}</p>
                      <code className="text-[11px] text-[#8B7355] bg-[#F7F4EF] px-2 py-0.5 rounded font-mono">{row.value}</code>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right: Preview ── */}
            <div className="flex-1 min-w-0">

              {/* Preview header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs text-[#9A9A9A] uppercase tracking-widest">Previewing</p>
                  <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{previewEntry.card.name || 'Unnamed'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDigitalExportCard(previewEntry.card)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#C9A96E]/15 text-[#8B7355] border border-[#C9A96E]/30 text-xs font-medium hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-smartphone-line text-sm"></i>
                    Share Digital Card
                  </button>
                  <div className="flex items-center gap-1 bg-white border border-[#E8E6E3] rounded-full p-1">
                    {(['front', 'back'] as CardSide[]).map(side => (
                      <button key={side} onClick={() => setActiveSide(side)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap capitalize ${activeSide === side ? 'bg-[#2C2C2C] text-white' : 'text-[#6B6B6B] hover:text-[#2C2C2C]'}`}>
                        {side}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Large preview */}
              <div className="mb-8">
                <div className="inline-block rounded-xl overflow-hidden" style={{ padding: 32, background: activeSide === 'front' ? (isDark ? '#E8E6E3' : '#3A3A3A') : (isDark ? '#D8D5D0' : '#2A2A2A') }}>
                  {activeSide === 'front'
                    ? <CardFront card={previewEntry.card} variant={previewEntry.variant} scale={1.5} />
                    : <CardBack card={previewEntry.card} variant={previewEntry.variant} scale={1.5} />
                  }
                </div>
              </div>

              {/* All cards thumbnail strip */}
              <div className="mb-8">
                <p className="text-xs text-[#9A9A9A] uppercase tracking-widest mb-3">All Cards — Click to Preview</p>
                <div className="flex flex-wrap gap-3">
                  {entries.map((entry) => {
                    const entryIsDark = entry.variant === 'dark-on-white' || entry.variant === 'dark-on-cream';
                    const isActive = previewEntry.id === entry.id;
                    const slug = getCardSlug(entry.card.name);
                    const isCopied = copiedLinkId === entry.id;
                    // card thumbnail width = CARD_W * 96 * 0.45 + 2*5 padding = ~161px
                    const thumbW = Math.round(3.5 * 96 * 0.45 + 10);
                    return (
                      <div key={entry.id} className="flex flex-col gap-1" style={{ width: thumbW }}>
                        {/* Thumbnail — click to preview */}
                        <button
                          onClick={() => { setPreviewEntry(entry); setExpandedId(entry.id); }}
                          className={`flex flex-col gap-1 p-2 rounded-lg border transition-all cursor-pointer w-full ${isActive ? 'border-[#C9A96E] ring-1 ring-[#C9A96E]' : 'border-[#E8E6E3] hover:border-[#2C2C2C]'}`}
                        >
                          <div className="rounded overflow-hidden" style={{ padding: 5, background: entryIsDark ? '#E8E6E3' : '#3A3A3A' }}>
                            <CardFront card={entry.card} variant={entry.variant} scale={0.45} />
                          </div>
                          <p className="text-[9px] text-[#6B6B6B] text-center truncate w-full">{entry.card.name || 'Unnamed'}</p>
                          <p className="text-[9px] text-[#C9A96E] text-center">×{entry.qty}</p>
                        </button>

                        {/* Action row: Share + Copy Link + View */}
                        <div className="flex gap-1 w-full">
                          {/* Share / Digital export */}
                          <button
                            onClick={() => setDigitalExportCard(entry.card)}
                            title="Open digital card export"
                            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md bg-[#F7F4EF] hover:bg-[#C9A96E]/20 text-[#8B7355] text-[9px] font-medium transition-colors cursor-pointer whitespace-nowrap border border-[#E8E6E3] hover:border-[#C9A96E]/40"
                          >
                            <i className="ri-smartphone-line" style={{ fontSize: 10 }}></i>
                            Share
                          </button>

                          {slug ? (
                            <>
                              {/* Copy shareable link */}
                              <button
                                onClick={() => handleCopyCardLink(entry)}
                                title={isCopied ? 'Link copied!' : `Copy link: /card/${slug}`}
                                className={`flex items-center justify-center gap-1 py-1.5 px-2 rounded-md text-[9px] font-medium transition-all cursor-pointer whitespace-nowrap border ${
                                  isCopied
                                    ? 'bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30'
                                    : 'bg-[#F7F4EF] hover:bg-[#2C2C2C]/8 text-[#6B6B6B] hover:text-[#2C2C2C] border-[#E8E6E3] hover:border-[#2C2C2C]/30'
                                }`}
                              >
                                <i className={isCopied ? 'ri-check-line' : 'ri-link'} style={{ fontSize: 10 }}></i>
                                {isCopied ? 'Copied' : 'Link'}
                              </button>

                              {/* View public card in new tab */}
                              <a
                                href={`/card/${slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Preview public card: /card/${slug}`}
                                className="flex items-center justify-center px-2 py-1.5 rounded-md bg-[#F7F4EF] hover:bg-[#1A1A1A] text-[#6B6B6B] hover:text-white text-[9px] font-medium transition-all cursor-pointer whitespace-nowrap border border-[#E8E6E3] hover:border-[#1A1A1A]"
                              >
                                <i className="ri-external-link-line" style={{ fontSize: 10 }}></i>
                              </a>
                            </>
                          ) : (
                            <div
                              title="No public link — only saved team members have shareable links"
                              className="flex items-center justify-center px-2 py-1.5 rounded-md border border-dashed border-[#E8E6E3] text-[#D0CCC7] cursor-default"
                            >
                              <i className="ri-link" style={{ fontSize: 10 }}></i>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Print sheet preview */}
              <div className="mb-8">
                <p className="text-xs text-[#9A9A9A] uppercase tracking-widest mb-3">
                  Print Sheet — {SHEET_SLOTS}-up on US Letter
                </p>
                <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-auto p-4">
                  <div style={{ transform: 'scale(0.38)', transformOrigin: 'top left', width: 8.5 * 96, height: 11 * 96 }}>
                    <MultiPrintSheet entries={entries} />
                  </div>
                  <div style={{ height: 11 * 96 * 0.38 - 4 }} />
                </div>
                <p className="text-[11px] text-[#9A9A9A] mt-2">Left = front · Right = back · Mixed team members fill rows top to bottom</p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mb-8">
                <button onClick={handlePrint} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#2C2C2C] text-white text-xs font-medium tracking-wide hover:bg-[#C9A96E] transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-printer-line text-sm"></i>Print / Save as PDF
                </button>
                <button
                  onClick={() => setDigitalExportCard(previewEntry.card)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-[#2C2C2C] border border-[#2C2C2C] text-xs font-medium tracking-wide hover:bg-[#F7F4EF] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-smartphone-line text-sm"></i>
                  Digital / iPhone Share
                </button>
                <button onClick={handleCopySpecs} className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap border ${copied ? 'bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30' : 'bg-white text-[#2C2C2C] border-[#2C2C2C] hover:bg-[#F7F4EF]'}`}>
                  <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
                  {copied ? 'Copied!' : 'Copy sheet summary'}
                </button>
              </div>

              {/* Print instructions */}
              <div className="bg-white rounded-lg border border-[#E8E6E3] p-6">
                <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">How to print</p>
                <ol className="space-y-3">
                  {[
                    'Click "Print / Save as PDF". In the print dialog choose "Save as PDF" to get a file for your printer.',
                    'Set paper to US Letter (8.5" × 11"), scale 100% — never "fit to page".',
                    'Print double-sided, flip on short edge, so front and back of each row align.',
                    'Mixed designs print automatically — each row uses the correct person\'s card.',
                    'Cut along crop marks. Send to a print shop for 16pt card stock with soft-touch laminate.',
                  ].map((text, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#2C2C2C] text-white text-[10px] flex-shrink-0 mt-0.5">{i + 1}</span>
                      <p className="text-xs text-[#6B6B6B] leading-relaxed">{text}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
